import { useId, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useDebounced } from '@/api/useDebounced';
import { useLanguage } from '@/i18n/LanguageContext';
import { StockActionDialog, type StockTarget } from '@/components/StockActionDialog';
import { ActionError, describeActionError } from '@/pages/Exceptions/shared';
import { SkuMeta } from '@/components/SkuMeta';
import type { StockSkuHit } from '@/api/types';

/**
 * "Found stock": the system shows 0 (or nothing) but the item is on the shelf.
 * Pick the product by manual search - EVERY hit shows its seller, so the owner
 * is chosen on purpose - then the location, then count it with reason FOUND.
 */
export function FoundStockPanel({ onClose, onDone }: { onClose: () => void; onDone: () => void }): JSX.Element {
  const { t } = useLanguage();
  const uid = useId();
  const [q, setQ] = useState('');
  const debounced = useDebounced(q.trim(), 250);
  const [sku, setSku] = useState<StockSkuHit | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [target, setTarget] = useState<StockTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const hits = useApi(
    () => (debounced.length >= 2 ? endpoints.stockSkuSearch({ q: debounced }) : Promise.resolve(null)),
    [debounced],
  );
  const warehouses = useApi(() => endpoints.warehouseSetup(), []);
  const whList = warehouses.status === 'success' ? warehouses.data.warehouses : [];
  const effectiveWarehouse = warehouseId || (whList.length === 1 ? (whList[0]?.id ?? '') : '');
  const structure = useApi(
    () => (sku && effectiveWarehouse ? endpoints.warehouseStructure(effectiveWarehouse) : Promise.resolve(null)),
    [sku?.sku_id, effectiveWarehouse],
  );
  const locations = structure.status === 'success' && structure.data ? [...structure.data.cells, ...structure.data.otherLocations] : [];

  async function onContinue(): Promise<void> {
    if (!sku || !effectiveWarehouse || !locationId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await endpoints.stockPositions({ warehouseId: effectiveWarehouse, locationId, skuId: sku.sku_id, ownerId: sku.owner_id });
      const here = res.positions.find((p) => p.lot_id === null) ?? null;
      const loc = locations.find((l) => l.id === locationId);
      setTarget({
        warehouseId: effectiveWarehouse,
        locationId,
        locationCode: loc?.code ?? '',
        ownerId: sku.owner_id,
        ownerName: sku.owner_name,
        skuId: sku.sku_id,
        skuLabel: `${sku.code} — ${sku.name}`,
        lotId: null,
        systemQty: here ? here.qty_on_hand : '0',
      });
    } catch (err) {
      setError(describeActionError(t, err));
    } finally {
      setBusy(false);
    }
  }

  if (target) {
    return (
      <StockActionDialog
        mode="COUNT"
        target={target}
        fixedReason="FOUND"
        onClose={onClose}
        onDone={onDone}
      />
    );
  }

  const hitList = hits.status === 'success' && hits.data ? hits.data.skus : [];

  return (
    <section className="card stock-panel" role="dialog" aria-label={t('found.title')}>
      <h2 className="card-title">{t('found.title')}</h2>
      <p className="field-hint">{t('found.intro')}</p>

      {!sku ? (
        <div className="order-create">
          <div className="field-row">
            <label htmlFor={`${uid}-q`}>{t('found.search')}</label>
            <input id={`${uid}-q`} value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
            <p className="field-hint">{t('found.searchHint')}</p>
          </div>
          {hits.status === 'error' && <ActionError message={describeActionError(t, hits.error)} />}
          {debounced.length >= 2 && hits.status === 'success' && hitList.length === 0 && <p>{t('found.noHits')}</p>}
          {hitList.length > 0 && (
            <ul className="sku-results">
              {hitList.map((h) => {
                const tracked = h.is_lot_tracked || h.is_serial_tracked;
                return (
                  <li key={`${h.sku_id}:${h.owner_id}`}>
                    <div>
                      <div>
                        {h.code} — {h.name}
                      </div>
                      <SkuMeta code={h.code} article={h.article} size={h.size} color={h.color} barcodes={h.barcodes} />
                      <div className="meta">{t('found.seller', { name: h.owner_name })}</div>
                      {tracked && <div className="meta">{t('stx.trackedHint')}</div>}
                    </div>
                    <button type="button" onClick={() => setSku(h)} disabled={tracked} aria-label={`${t('found.select')}: ${h.code}, ${h.owner_name}`}>
                      {t('found.select')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="form-actions">
            <button type="button" onClick={onClose}>
              {t('stx.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="order-create">
          <p style={{ fontWeight: 600 }}>{t('found.selected', { sku: `${sku.code} — ${sku.name}`, owner: sku.owner_name })}</p>
          <button type="button" className="link-button" onClick={() => { setSku(null); setLocationId(''); }}>
            {t('found.change')}
          </button>
          {warehouses.status === 'error' && <ActionError message={describeActionError(t, warehouses.error)} />}
          {whList.length > 1 && (
            <div className="field-row">
              <label htmlFor={`${uid}-wh`}>{t('found.warehouse')}</label>
              <select id={`${uid}-wh`} value={effectiveWarehouse} onChange={(e) => { setWarehouseId(e.target.value); setLocationId(''); }}>
                <option value="">{t('found.warehouse')}</option>
                {whList.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="field-row">
            <label htmlFor={`${uid}-loc`}>{t('found.location')}</label>
            <select id={`${uid}-loc`} value={locationId} onChange={(e) => setLocationId(e.target.value)} disabled={!effectiveWarehouse || structure.status !== 'success'}>
              <option value="">{t('stx.destinationPlaceholder')}</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{'zone_name' in l && l.zone_name ? `${l.zone_name} · ${l.code}` : l.code}</option>
              ))}
            </select>
            {structure.status === 'error' && <ActionError message={describeActionError(t, structure.error)} />}
          </div>
          {error && <ActionError message={error} />}
          <div className="form-actions">
            <button type="button" className="primary" onClick={() => void onContinue()} disabled={!locationId || busy}>
              {busy ? t('stx.working') : t('stx.continue')}
            </button>
            <button type="button" onClick={onClose}>
              {t('stx.cancel')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
